/**
 * Canonical "current action plan" selection, shared across repos: prefer the
 * active plan (the one the clinician edits), falling back to the most-recent
 * completed by period end. The Fountain Life member app (`useLatestCarePlan`)
 * and the agents service (`PatientServiceClient.get_care_plan(mode="latest")`)
 * mirror this ordering so Zori, clinicians, and members all act on the same
 * CarePlan. `CarePlan.status` never gates member visibility; per-activity
 * `detail.status` does (`unknown` = visible, `on-hold` = hidden). Keep these
 * three implementations in sync.
 */
export const compareActionPlansForLatestView = (
  a: fhir.CarePlan,
  b: fhir.CarePlan,
): number => {
  // Prefer the current active plan; completed plans are the latest fallback.
  if (a.status === 'active' && b.status !== 'active') return -1;
  if (a.status !== 'active' && b.status === 'active') return 1;
  if (a.status === 'completed' && b.status !== 'completed') return -1;
  if (a.status !== 'completed' && b.status === 'completed') return 1;
  if (a.status === 'entered-in-error' && b.status !== 'entered-in-error')
    return 1;
  if (a.status !== 'entered-in-error' && b.status === 'entered-in-error')
    return -1;
  if (!a.period?.end) return -1;
  if (!b.period?.end) return 1;
  return new Date(b.period.end).getTime() - new Date(a.period.end).getTime();
};

export const findLatestActionPlan = (
  carePlans: fhir.CarePlan[] | undefined,
): fhir.CarePlan | undefined =>
  carePlans
    ?.filter(carePlan => carePlan.supportingInfo?.length)
    .slice()
    .sort(compareActionPlansForLatestView)[0];
